"use client";

import BottomCta from "@/components/BottomCta";
import Link from "next/link";
import { motion } from "framer-motion";
import { Megaphone, MousePointer, Search, Globe, PenTool, BarChart2, CheckCircle2 } from "lucide-react";

export default function DigitalMarketingPage() {
  return (
    <main className="bg-[#FAFAFA] min-h-screen w-full overflow-x-hidden text-[#000000]">

      {/* ── HERO SECTION ── */}
      <section className="relative min-h-[85vh] flex items-center justify-center py-20 lg:py-32 2xl:py-48 overflow-hidden">
        <div className="max-w-4xl xl:max-w-6xl 2xl:max-w-7xl mx-auto w-full px-6 text-center flex flex-col items-center relative z-10">
          
          <motion.div
            initial={{ opacity: 0, y: -15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-[#B8893A]/20 bg-[#B8893A]/5 mb-8"
          >
            <span className="w-2 h-2 rounded-full bg-[#B8893A]" />
            <span className="text-[10px] sm:text-xs font-bold text-[#B8893A] uppercase tracking-[0.2em]">
              Digital Marketing Services
            </span>
          </motion.div>

          <motion.h1 
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className="text-4xl sm:text-5xl md:text-6xl xl:text-7xl 2xl:text-8xl font-bold tracking-tight leading-[1.15] mb-6 max-w-5xl text-[#000000]"
          >
            Reach More. <br />
            <span className="font-serif font-normal text-[#B8893A]">Grow Faster.</span>
          </motion.h1>

          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="text-gray-500 text-base sm:text-lg md:text-xl xl:text-2xl max-w-2xl xl:max-w-4xl font-light leading-relaxed mb-10"
          >
            We help businesses build a strong online presence, attract the right audience, and turn attention into real results. Our digital marketing services are designed to support growth with clear strategies and consistent execution.
          </motion.p>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
            <Link href="/contact" className="group inline-flex w-full sm:w-auto items-center justify-center px-10 py-4 xl:px-14 xl:py-5 font-bold text-white bg-[#B8893A] rounded-xl transition-all duration-300 hover:bg-[#a3762f] hover:scale-[1.03] active:scale-[0.98] text-base xl:text-lg tracking-wide">
              Get Started
            </Link>
            <Link href="/services" className="group inline-flex w-full sm:w-auto items-center justify-center px-10 py-4 xl:px-14 xl:py-5 font-bold text-[#B8893A] bg-transparent border-2 border-[#B8893A] rounded-xl transition-all duration-300 hover:bg-[#B8893A]/5 hover:scale-[1.03] active:scale-[0.98] text-base xl:text-lg tracking-wide">
              Our Services
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ── SCROLLING TAG STRIP ── */}
      <section className="py-8 border-y border-gray-100 bg-white overflow-hidden flex flex-nowrap items-center w-full">
        <div className="flex animate-[scroll-left_24s_linear_infinite] whitespace-nowrap min-w-full items-center">
          {[
            "Social Media", "SEO", "Google Ads", "Branding", "Content Creation", "Analytics", "Strategy", "Growth",
            "Social Media", "SEO", "Google Ads", "Branding", "Content Creation", "Analytics", "Strategy", "Growth",
          ].map((item, i) => (
            <div key={i} className="flex items-center justify-center mx-6">
              {i % 2 === 0 && <span className="text-[#B8893A] mr-2">✦</span>}
              <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#000000]">{item}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── WHY IT MATTERS ── */}
      <section className="px-6 sm:px-12 lg:px-16 py-20 xl:py-32 bg-[#FAFAFA]">
        <div className="max-w-[1600px] mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs font-bold text-[#B8893A] uppercase tracking-[0.2em] mb-4">Why It Matters</div>
            <h2 className="text-3xl sm:text-4xl xl:text-5xl font-black tracking-tight mb-4">
              Be Seen Where <br /><span className="font-serif font-normal text-[#B8893A]">It Counts</span>
            </h2>
            <div className="w-12 h-[3px] bg-[#B8893A] mb-6 rounded-full" />
            <p className="text-gray-500 font-light leading-relaxed mb-8">
              A strong digital presence helps your business stay competitive and relevant in an increasingly crowded market. The right strategy makes all the difference.
            </p>
          </div>
          <div>
            {[
              "Reach your target audience effectively",
              "Increase brand awareness and visibility",
              "Generate quality leads and inquiries",
              "Build trust with potential customers",
              "Support long-term business growth",
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-4 mb-4">
                <div className="w-5 h-5 border border-[#B8893A] rounded flex items-center justify-center shrink-0 mt-0.5">
                  <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                    <path d="M1 4L3.5 6.5L9 1" stroke="#c9a84c" strokeWidth="1.5" />
                  </svg>
                </div>
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHAT WE OFFER (SERVICES) ── */}
      <section id="services" className="px-6 sm:px-12 lg:px-16 py-20 xl:py-32 bg-white border-y border-gray-100">
        <div className="max-w-[1600px] mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl xl:text-5xl font-black tracking-tight mb-4">Complete Digital <br /><span className="font-serif font-normal text-[#B8893A]">Growth Solutions</span></h2>
            <p className="text-gray-400 font-medium xl:text-lg uppercase tracking-wider text-xs">What We Offer</p>
            <div className="w-12 h-[3px] bg-[#B8893A] mx-auto mt-4 rounded-full" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 xl:gap-8">
            {[
              { icon: Megaphone, title: "Social Media Management", text: "Content creation, scheduling, and community management across all major platforms." },
              { icon: MousePointer, title: "Paid Advertising", text: "Targeted Google and social media campaigns designed to maximise ROI." },
              { icon: Search, title: "Search Engine Optimisation", text: "On-page and off-page SEO to improve rankings and drive organic traffic." },
              { icon: Globe, title: "Website Content & Optimisation", text: "Compelling copy and technical improvements that convert visitors into customers." },
              { icon: PenTool, title: "Branding & Digital Strategy", text: "Clear positioning, visual identity, and strategic planning for sustainable growth." },
              { icon: BarChart2, title: "Performance Tracking & Reporting", text: "Transparent analytics and regular reports so you always know what is working." },
            ].map((item, i) => {
              const IconComponent = item.icon;
              return (
                <div key={i} className="group bg-white p-8 rounded-2xl border border-gray-100/80 transition-all duration-300 hover:shadow-md flex flex-col justify-between">
                  <div>
                    <div className="text-xs font-bold text-[#B8893A] uppercase tracking-[0.2em] mb-4">0{i + 1}</div>
                    <div className="w-10 h-10 rounded-xl bg-[#B8893A]/5 flex items-center justify-center text-[#B8893A] mb-6 group-hover:bg-[#B8893A] group-hover:text-white transition-all duration-300">
                      <IconComponent className="w-5 h-5" />
                    </div>
                    <h3 className="text-xl font-bold mb-2 group-hover:text-[#B8893A] transition-colors duration-300">{item.title}</h3>
                    <p className="text-gray-500 font-light text-sm leading-relaxed">{item.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── OUR APPROACH ── */}
      <section className="px-6 sm:px-12 lg:px-16 py-20 xl:py-32 bg-[#FAFAFA]">
        <div className="max-w-[1600px] mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-3xl sm:text-4xl xl:text-5xl font-black tracking-tight mb-4">Simple Strategy.<br /><span className="font-serif font-normal text-[#B8893A]">Real Results.</span></h2>
            <p className="text-gray-400 font-medium xl:text-lg uppercase tracking-wider text-xs">Our Approach</p>
            <div className="w-12 h-[3px] bg-[#B8893A] mx-auto mt-4 rounded-full" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8">
            {[
              { num: "01", title: "Understanding Your Business", text: "We learn about your goals, audience, and market position." },
              { num: "02", title: "Strategy Planning", text: "We build a focused, actionable marketing plan tailored to your needs." },
              { num: "03", title: "Execution", text: "We manage campaigns, content creation, and promotions end-to-end." },
              { num: "04", title: "Optimisation", text: "We track performance continuously and refine for better results." },
            ].map((step, i) => (
              <div key={i} className="relative group">
                <div className="text-6xl xl:text-7xl font-black text-gray-100 group-hover:text-[#B8893A]/10 transition-colors duration-300 mb-4">{step.num}</div>
                <div className="border-t border-gray-200 pt-6 mt-2">
                  <h3 className="text-lg font-bold mb-2 tracking-tight">{step.title}</h3>
                  <p className="text-gray-500 font-light text-sm leading-relaxed">{step.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHO WE HELP & WHY CHOOSE US ── */}
      <section className="px-6 sm:px-12 lg:px-16 py-20 bg-white border-t border-gray-100">
        <div className="max-w-[1600px] mx-auto grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="text-xs font-bold text-[#B8893A] uppercase tracking-[0.2em] mb-4">Who We Help</div>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4">Built for Businesses<br /><span className="font-serif font-normal text-[#B8893A]">Ready to Grow</span></h2>
            <div className="w-12 h-[3px] bg-[#B8893A] mb-6 rounded-full" />
            <div className="flex flex-col gap-3">
              {[
                "Startups building their online presence",
                "Small and medium businesses",
                "Service-based companies",
                "Businesses looking to generate more leads",
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 p-3 bg-[#FAFAFA] border border-gray-100 rounded-xl">
                  <CheckCircle2 className="w-5 h-5 text-[#B8893A] shrink-0" />
                  <span className="text-sm font-semibold text-gray-700">{item}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs font-bold text-[#B8893A] uppercase tracking-[0.2em] mb-4">Why Choose Us</div>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4">Focused on<br /><span className="font-serif font-normal text-[#B8893A]">Your Growth</span></h2>
            <div className="w-12 h-[3px] bg-[#B8893A] mb-6 rounded-full" />
            <div className="grid sm:grid-cols-1 gap-3">
              {[
                "Clear and practical marketing strategies",
                "Consistent and reliable execution",
                "Results-driven approach",
                "Transparent communication and reporting",
                "Designed to attract real customers, not just clicks",
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 p-3 bg-[#FAFAFA] border border-gray-100 rounded-xl">
                  <div className="w-2 h-2 shrink-0 bg-[#B8893A] mt-0.5 rounded-full" />
                  <span className="text-sm font-semibold text-gray-700">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── BOTTOM CTA ── */}
      <section id="contact" className="px-6 py-24 bg-[#FAFAFA] border-t border-gray-100 text-center">
        <div className="max-w-3xl mx-auto">
          <div className="text-xs font-bold text-[#B8893A] uppercase tracking-[0.2em] mb-4">Get Noticed. Get Results.</div>
          <h2 className="text-4xl sm:text-5xl xl:text-6xl font-black tracking-tight mb-6">
            Turn Visibility Into <br />
            <span className="font-serif font-normal text-[#B8893A]">Opportunity</span>
          </h2>
          <p className="text-gray-500 font-light leading-relaxed mb-10 max-w-xl mx-auto">
            Let your business reach the right audience with the right strategy. We don't just market your business — we help it grow.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="https://wa.me/971586009983" target="_blank" rel="noopener noreferrer" className="group inline-flex w-full sm:w-auto items-center justify-center px-10 py-4 xl:px-14 xl:py-5 font-bold text-white bg-[#B8893A] rounded-xl transition-all duration-300 hover:bg-[#a3762f] hover:scale-[1.03] active:scale-[0.98] text-base xl:text-lg tracking-wide">
              Call Now
            </a>
            <a href="https://wa.me/971586009983" target="_blank" rel="noopener noreferrer" className="group inline-flex w-full sm:w-auto items-center justify-center px-10 py-4 xl:px-14 xl:py-5 font-bold text-[#B8893A] bg-transparent border-2 border-[#B8893A] rounded-xl transition-all duration-300 hover:bg-[#B8893A]/5 hover:scale-[1.03] active:scale-[0.98] text-base xl:text-lg tracking-wide">
              WhatsApp Us
            </a>
          </div>
        </div>
      </section>

    </main>
  );
}